import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ProductSpec, ProductVariant } from '@/types/product';
import { addVariant, removeVariant } from './productEditorState';
import ProductDescriptionEditor from './ProductDescriptionEditor';

interface ProductVariantsFieldsProps {
  spec: ProductSpec;
  activeVariantId: string;
  onSelect: (id: string) => void;
  onChange: (spec: ProductSpec) => void;
}

export default function ProductVariantsFields({
  spec,
  activeVariantId,
  onSelect,
  onChange,
}: ProductVariantsFieldsProps) {
  const [variantToDelete, setVariantToDelete] = useState<ProductVariant | null>(null);
  const activeVariant =
    spec.variants.find((variant) => variant.id === activeVariantId) ?? spec.variants[0];
  const patchVariant = (patch: Partial<ProductVariant>) => {
    if (!activeVariant) return;
    onChange({
      ...spec,
      variants: spec.variants.map((variant) =>
        variant.id === activeVariant.id ? { ...variant, ...patch } : variant
      ),
    });
  };

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Varianten</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Kies de variant waarvoor je de omschrijving en kostprijs instelt.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            const next = addVariant(spec, `Variant ${spec.variants.length + 1}`);
            onChange(next);
            onSelect(next.variants[next.variants.length - 1].id);
          }}
        >
          <Plus className="h-3.5 w-3.5" />
          Variant toevoegen
        </Button>
      </div>
      <div role="group" aria-label="Variant kiezen" className="flex flex-wrap gap-2">
        {spec.variants.map((variant) => (
          <Button
            key={variant.id}
            type="button"
            variant={variant.id === activeVariant?.id ? 'secondary' : 'outline'}
            size="sm"
            className={`h-auto min-h-8 max-w-full whitespace-normal break-words text-left ${
              variant.id === activeVariant?.id
                ? 'border border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/15 hover:bg-blue-100'
                : ''
            }`}
            aria-pressed={variant.id === activeVariant?.id}
            onClick={() => onSelect(variant.id)}
          >
            {variant.name || 'Naamloze variant'}
          </Button>
        ))}
      </div>
      {activeVariant && (
        <>
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1 space-y-1.5">
              <Label htmlFor="variant-name">Variantnaam voor zoeken</Label>
              <Input
                id="variant-name"
                value={activeVariant.name}
                onChange={(event) => patchVariant({ name: event.target.value })}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Variant verwijderen"
              title="Variant verwijderen"
              disabled={spec.variants.length <= 1}
              onClick={() => setVariantToDelete(activeVariant)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-2">
            <Label>Regels in de offerte (alle varianten)</Label>
            <Tabs
              value={spec.rowMode ?? 'collapsed'}
              onValueChange={(value) =>
                onChange({ ...spec, rowMode: value as ProductSpec['rowMode'] })
              }
            >
              <TabsList className="h-auto max-w-full">
                <TabsTrigger value="collapsed">Eén regel</TabsTrigger>
                <TabsTrigger value="per_line" className="whitespace-normal">
                  Regel per prijsregel
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          {(spec.rowMode ?? 'collapsed') === 'collapsed' || !spec.lines.length ? (
            <div className="space-y-1.5">
              <Label htmlFor={`variant-description-${activeVariant.id}`}>
                Omschrijving in de offerte
              </Label>
              <ProductDescriptionEditor
                key={activeVariant.id}
                id={`variant-description-${activeVariant.id}`}
                value={activeVariant.descriptionHtml ?? ''}
                onChange={(descriptionHtml) => patchVariant({ descriptionHtml })}
              />
              <p className="text-xs text-muted-foreground">
                Deze tekst is de volledige omschrijving van de prijsregel in de offerte. Voeg hier
                dus ook de producttitel toe. Laat het veld leeg om de productnaam te gebruiken.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Omschrijvingen per offerteregel (alle varianten).
              </p>
              {spec.lines.map((line, index) => (
                <div key={line.id} className="space-y-1.5">
                  <Label htmlFor={`line-description-${line.id}`}>Regel {index + 1}</Label>
                  <Input
                    id={`line-description-${line.id}`}
                    value={line.description}
                    onChange={(event) =>
                      onChange({
                        ...spec,
                        lines: spec.lines.map((item) =>
                          item.id === line.id ? { ...item, description: event.target.value } : item
                        ),
                      })
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
      <Dialog
        open={Boolean(variantToDelete)}
        onOpenChange={(open) => !open && setVariantToDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Variant verwijderen?</DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            De omschrijving, uren en bedragen van <strong>{variantToDelete?.name}</strong> worden
            uit het concept verwijderd.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVariantToDelete(null)}>
              Annuleren
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (variantToDelete) {
                  const next = removeVariant(spec, variantToDelete.id);
                  onChange(next);
                  if (variantToDelete.id === activeVariant?.id) {
                    onSelect(next.variants[0].id);
                  }
                }
                setVariantToDelete(null);
              }}
            >
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
